import { Box, TextField, Typography, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";

export const PageContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

export const ComposerLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  minHeight: 0,
  flex: 1,
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

export const AuthoringColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== "widthPercent",
})<{ widthPercent?: number }>(({ theme, widthPercent = 60 }) => ({
  flex: `1 1 ${widthPercent}%`,
  minWidth: 0,
  minHeight: 0,
  overflowY: "auto",
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(6),
  [theme.breakpoints.down("md")]: {
    flex: "1 1 auto",
    width: "100%",
  },
}));

export const PreviewColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== "widthPercent",
})<{ widthPercent?: number }>(({ theme, widthPercent = 40 }) => ({
  flex: `1 1 ${widthPercent}%`,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: theme.palette.background["paper-elevation-reversed"],
  padding: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    flex: "1 1 auto",
    width: "100%",
  },
}));

export const AuthoringStack = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(6),
}));

export const SectionBlock = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
}));

export const SurfaceCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background["paper-elevation-1"],
  borderRadius: `${theme.shape.borderRadiusMd}px`,
  padding: theme.spacing(6),
}));

export const CreateMessageShell = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background["paper-elevation-0"],
  borderRadius: `${theme.shape.borderRadiusLg}px`,
  padding: theme.spacing(6),
}));

export const CreateMessageStack = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
}));

export const MessageInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.common.white,
  borderRadius: `${theme.shape.borderRadiusMd}px`,
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: theme.palette.tokens.outlines.defaultBordersDark,
  padding: theme.spacing(4),
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
  },
}));

/** Scrolls only the editor; keep stats bar (character count) fixed below. */
export const MessageEditorScrollArea = styled(Box)(() => ({
  minHeight: "175px",
  maxHeight: "250px",
  overflowY: "auto",
}));

export const StyledMessageInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    padding: 0,
    alignItems: "flex-start",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-input": {
    padding: `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(4)}`,
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.4,
    },
  },
}));

export const PreviewText = styled(Typography)({
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

export const PreviewImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: 320,
  borderRadius: `${theme.shape.borderRadiusMd}px`,
  objectFit: "cover",
}));

export const AuthoringStatePreview = styled("pre")(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(3),
  padding: theme.spacing(4),
  borderRadius: `${theme.shape.borderRadiusMd}px`,
  backgroundColor: alpha(theme.palette.common.black, 0.12),
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  color: theme.palette.text.contrastText,
}));
